import styled from "styled-components";
import { Text } from "../../components/Text";
import { useSettings } from "../../Settings";
import range from "lodash/range";
import { Flex } from "../../components/Flex";
import { Select } from "../../components/Select";
import { TimeFormat } from "@aika/types";
import React from "react";

const SettingsLabel = styled.label`
  width: 150px;
  display: block;
  font-weight: 600;
`;

const toHourMinute = (x: number, format: TimeFormat) => {
  let hours = Math.floor(x / 60);
  let minutes = x % 60;
  if (x === 0) {
    return "Starting Midnight";
  }
  if (x === 1440) {
    return "Ending Midnight";
  }
  let suffix =
    format === TimeFormat.TwelveHour ? (hours >= 12 ? " PM" : " AM") : "";
  if (format === TimeFormat.TwelveHour && hours > 12) {
    hours = hours - 12;
  }

  return (
    hours.toString().padStart(2, "0") +
    ":" +
    minutes.toString().padStart(2, "0") +
    suffix
  );
};

const dayRangeOptions = [
  {
    value: "0",
    label: "Calendar Week",
  },
  {
    value: "1",
    label: "Just Today",
  },
  {
    value: "2",
    label: "Yesterday + 6 Days",
  },
  {
    value: "3",
    label: "Yesterday + 2 Days",
  },
  {
    value: "4",
    label: "Working Week",
  },
  {
    value: "5",
    label: "Calendar Week (Sunday Start)",
  },
];

const timeFormatOptions = [
  {
    value: "0",
    label: "24-Hour",
  },
  {
    value: "1",
    label: "12-Hour",
  },
];

const GeneralSettings = () => {
  const [settings, setSettings] = useSettings();

  const changeStartOfDay = (ev: React.ChangeEvent<HTMLSelectElement>) => {
    const startOfDay = parseInt(ev.currentTarget.value, 10);
    const endOfDay = Math.max(settings.endOfDay, startOfDay + 30);
    setSettings({
      ...settings,
      startOfDay,
      endOfDay,
    });
  };

  const changeEndOfDay = (ev: React.ChangeEvent<HTMLSelectElement>) => {
    const endOfDay = parseInt(ev.currentTarget.value, 10);
    const startOfDay = Math.min(endOfDay - 30, settings.startOfDay);
    setSettings({
      ...settings,
      startOfDay,
      endOfDay,
    });
  };

  const changeDayRange = (ev: React.ChangeEvent<HTMLSelectElement>) => {
    const dayRange = parseInt(ev.currentTarget.value, 10);
    setSettings({
      ...settings,
      dayRange,
    });
  };

  const changeTimeFormat = (ev: React.ChangeEvent<HTMLSelectElement>) => {
    const timeFormat = parseInt(ev.currentTarget.value, 10);
    setSettings({
      ...settings,
      timeFormat,
    });
  };

  const startOfDayOptions = range(0, settings.endOfDay - 30, 30).map((x) => ({
    value: x.toString(),
    label: toHourMinute(x, settings.timeFormat),
  }));

  const endOfDayOptions = range(settings.startOfDay + 30, 1441, 30).map(
    (x) => ({
      value: x.toString(),
      label: toHourMinute(x, settings.timeFormat),
    })
  );

  return (
    <div>
      <Text fontSize={4} fontWeight={600} mb={2}>
        General Settings
      </Text>
      <Flex alignItems="center" mb={1}>
        <SettingsLabel>Start of Day</SettingsLabel>
        <Select
          value={settings.startOfDay}
          onChange={changeStartOfDay}
          options={startOfDayOptions}
          width={200}
        />
      </Flex>
      <Flex alignItems="center" mb={2}>
        <SettingsLabel>End of Day</SettingsLabel>
        <Select
          value={settings.endOfDay}
          onChange={changeEndOfDay}
          options={endOfDayOptions}
          width={200}
        />
      </Flex>
      <Text mb={2}>
        Note: Blocks outside this range will be hidden but still included on
        reports.
      </Text>
      <Flex alignItems="center" mb={1}>
        <SettingsLabel>Day Range</SettingsLabel>
        <Select
          value={settings.dayRange}
          onChange={changeDayRange}
          options={dayRangeOptions}
          width={200}
        />
      </Flex>
      <Flex alignItems="center" mb={2}>
        <SettingsLabel>Time Format</SettingsLabel>
        <Select
          value={settings.timeFormat}
          onChange={changeTimeFormat}
          options={timeFormatOptions}
          width={200}
        />
      </Flex>
    </div>
  );
};

export default GeneralSettings;
